import React, { useEffect, useRef, useState } from "react"

import { Form, Formik, useFormikContext } from "formik"
import moment from "moment-timezone"
import { pipe, subscribe } from "wonka"

import { css } from "@emotion/core"
import { gql } from "@urql/core"

import createBlob from "src/createBlob"
import client from "src/urql-client"

import {
  CheckboxField,
  FormFailures,
  SubmitButton,
  TimeZoneField,
  handleFailure,
} from "components/Forms/Formik/hookComponents"
import ModalWithProvidedBody from "components/ModalWithProvidedBody"

const SessionsExport = ({ canAdminBudgets }) => {
  const [status, setStatus] = useState()
  const [csvUrl, setCsvUrl] = useState()
  const csvDownloadLink = useRef()
  const { values: searchValues } = useFormikContext()
  const variables = { ...searchValues }
  if (variables.startsAt) {
    variables.startsAt = moment(variables.startsAt.toDate())
      .startOf("day")
      .format()
  }
  if (variables.endsAt) {
    variables.endsAt = moment(variables.endsAt.toDate()).endOf("day").format()
  }

  const handleExport = ({ timezone, useBillableName }, actions, closeModal) => {
    const { unsubscribe } = pipe(
      client.subscription(exportSubscription, {
        searchInput: variables,
        timezone,
        useBillableName,
      }),
      subscribe(result => {
        // triggered by backend exception
        if (result.error) {
          handleFailure(actions, [result.error])
          actions.setSubmitting(false)
          return
        }

        // triggered by graphql error
        if (result.errors) {
          handleFailure(actions, result.errors)
          actions.setSubmitting(false)
          return
        }

        const { status, failures, csv } = result.data.airtutorsSessionExport

        if (status === "done") {
          const blobUrl = createBlob(csv, "text/csv")
          setCsvUrl(blobUrl)
          actions.setSubmitting(false)
          unsubscribe()
          closeModal()
        } else if (status) {
          setStatus(status)
        }

        if (failures.length > 0) {
          handleFailure(actions, failures)
          unsubscribe()
        }
      })
    )
  }

  useEffect(() => {
    if (!csvUrl) return
    csvDownloadLink.current.click()
    setCsvUrl()
    setTimeout(() => URL.revokeObjectURL(csvUrl), 0)
  }, [csvUrl])

  return (
    <>
      <a
        ref={csvDownloadLink}
        href={csvUrl}
        download={`airtutors sessions export.csv`}
        css={css`
          display: none;
        `}
      >
        csv
      </a>

      <ModalWithProvidedBody
        modalTitle="Export"
        buttonText="Export"
        buttonClassName="btn btn-success"
        afterClose={() => {
          setStatus()
        }}
      >
        {({ closeModal }) => (
          <Formik
            initialValues={{ timezone: "Pacific", useBillableName: false }}
            onSubmit={(values, actions) =>
              handleExport(values, actions, closeModal)
            }
          >
            <Form>
              <div className="modal-body">
                <TimeZoneField
                  name="timezone"
                  menuPortalTarget={document.body}
                  styles={{
                    container: provided => ({
                      ...provided,
                      maxWidth: 250,
                    }),
                  }}
                />

                {canAdminBudgets && (
                  <CheckboxField
                    name="useBillableName"
                    label="Use Billable Name"
                  />
                )}
              </div>

              <div className="modal-footer flex-col">
                <FormFailures />
                {status && <p>Export State: {status}</p>}
                <SubmitButton text="Export" />
              </div>
            </Form>
          </Formik>
        )}
      </ModalWithProvidedBody>
    </>
  )
}

const exportSubscription = gql`
  subscription (
    $searchInput: AirtutorsSessionSearchInputObject!
    $timezone: TimeZoneEnum!
    $useBillableName: Boolean
  ) {
    airtutorsSessionExport(
      searchInput: $searchInput
      timezone: $timezone
      useBillableName: $useBillableName
    ) {
      status
      csv
      failures {
        message
      }
    }
  }
`

export default SessionsExport
